/**
 * Reviews Cross-Platform Calibration Constants (Story 23.7 — AC #1, #4, #17)
 *
 * Why the calibration footnote is SHORTER than Story 20.9's:
 *   Story 20.9's SEO Cross-Tool required a mandatory multi-paragraph
 *   footnote because Ahrefs DR + Semrush AS + Moz DA use proprietary models
 *   that produce different scores for identical sites — agency clients
 *   misread "DR 56 vs DA 50" as failure when it's scale calibration.
 *   Story 23.7's 3 review platforms ALL use a 0-5 decimal rating scale
 *   (Google star rating + Trustpilot TrustScore + Yelp business rating),
 *   so bar heights are DIRECTLY comparable without calibration. The
 *   footnote is therefore lightweight and only discloses the Yelp 3-excerpt
 *   cap so consumers don't misinterpret Yelp's review-count surface.
 *
 * Why these are static module-level constants (NOT API-sourced):
 *   Round 3 Red Team Attack #4 (carried forward from Story 20.9) — the
 *   cross-platform reviews widgets must never interpret per-platform
 *   integration metadata as label text. Sourcing display names or the
 *   calibration footnote from `integration.metadata.label` would let an
 *   attacker who controls integration metadata XSS into the cross-platform
 *   widget render path. HTML-escape at render time is still applied
 *   (defense-in-depth) but the primary defense is "never source these from
 *   user-influenced metadata".
 *
 * @see `_bmad-output/implementation-artifacts/23-7-reviews-unified-widget.md` AC #1
 */

/**
 * The lightweight calibration footnote rendered below Preset #1's first KPI
 * (Story 23.7 AC #4 — footnote on FIRST KPI only per Story 20.9 layout
 * precedent at widgetPresets.ts:3642-3644). 2-3 sentences disclosing:
 *   (a) all 3 platforms use the same 0-5 decimal scale → no normalization,
 *   (b) Yelp returns only 3-7 most-recent excerpts via Fusion API so its
 *       review-count totals reflect the full lifetime count while its
 *       velocity reflects the recent 30-day window.
 *
 * `as const` so any code attempting to mutate the export is rejected at
 * compile-time. The actual XSS defense at render time is the existing
 * `htmlEscape` helper from Story 20.3.
 */
export const REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE =
  `All 3 review platforms use a 0-5 decimal rating scale (Google's star rating, Trustpilot's TrustScore, Yelp's business rating). Bar heights and KPIs are directly comparable across platforms without normalization. Review counts and review velocity reflect each platform's documented review surface — Yelp returns only 3 most-recent excerpts via Fusion API per request (Plus/Enterprise tiers may surface more on dedicated endpoints), so its review-count totals reflect the full lifetime count while its velocity reflects the recent 30-day window.

Story 23.8 — Response Tracking widgets render 2 platforms only (Google Reviews + Trustpilot); Yelp Fusion has no reply API surface per Story 23.5 documented constraint. Reply-rate semantics differ between platforms — Google counts APPROVED-state replies only (PENDING / REJECTED replies are NOT bucketed into Google's replyRate per the adapter's APPROVED-only invariant); Trustpilot has no moderation states (companyReply non-null is the reply signal). The numbers ARE directly comparable as percentages-of-reviews-responded-to, but the underlying state-machine semantics differ.` as const;

/**
 * The 3-platform display names rendered as bar-chart x-axis labels,
 * line-chart legend entries, and KPI card titles. Round 3 Red Team Attack
 * #4 — NEVER source from API metadata; this static record is the single
 * source of truth.
 *
 * Type-narrowed to the 3 known platform keys so TypeScript catches typos
 * and any attempt to look up an attacker-controlled key at compile time.
 */
export const REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES: Record<
  "google-reviews" | "trustpilot" | "yelp",
  string
> = {
  "google-reviews": "Google Reviews",
  trustpilot: "Trustpilot",
  yelp: "Yelp",
};

/**
 * Per-platform brand colors for cross-platform bar / line chart rendering.
 * Sourced from each vendor's canonical brand-color style guide:
 *   - Google: `#4285F4` — Google brand canonical blue
 *   - Trustpilot: `#00B67A` — Trustpilot brand guideline canonical green
 *   - Yelp: `#D32323` — Yelp brand guideline canonical red
 *
 * Round 3 Red Team Attack #4 hardens these as the single source of truth
 * for cross-platform widget rendering. Getting them visibly wrong dilutes
 * the "we know reputation" credibility the cross-platform widget is meant
 * to establish.
 */
export const REVIEWS_CROSS_PLATFORM_BRAND_COLORS: Record<
  "google-reviews" | "trustpilot" | "yelp",
  string
> = {
  "google-reviews": "#4285F4",
  trustpilot: "#00B67A",
  yelp: "#D32323",
};

/**
 * Per-bucket sentiment colors for the Cross-Platform Sentiment Stack
 * widget (Story 23.7 Preset #4). Round 1 Code Review Blind Hunter Finding
 * "sentiment hex colors should be named constants" — the build-to-sell-
 * directive's "no magic numbers" principle promoted these from inline
 * literals to exported constants. Standard Tailwind palette tokens:
 *   - positive: green-600 (`#16A34A`)
 *   - neutral: amber-400 (`#FBBF24`)
 *   - negative: red-600 (`#DC2626`)
 *
 * Colorblind-safe per WebAIM Sim Daltonism check (verified 2026-05-19):
 * the 3-band positive/neutral/negative palette retains contrast under
 * deuteranopia + protanopia simulations.
 */
export const REVIEWS_CROSS_PLATFORM_SENTIMENT_COLORS: Record<
  "positive" | "neutral" | "negative",
  string
> = {
  positive: "#16A34A",
  neutral: "#FBBF24",
  negative: "#DC2626",
};

/**
 * Story 23.8 — Response-time band thresholds in HOURS for the Avg Response
 * Time Bar widget's color-banded rendering.
 *
 * Sourced from the May 2026 industry benchmarks:
 *   - ReplyOnTheFly 2026 Google Review Response Benchmark
 *     (replyonthefly.com/blog/2026-google-review-response-benchmark
 *     verified 2026-05-19) — "Top performers using automation respond in
 *     <2 hours (32x speed advantage). Average response time = 2.7 days."
 *   - BrightLocal 2026 Consumer Review Survey (cited in ReplyOnTheFly +
 *     business.trustpilot.com/features/respond-to-reviews verified
 *     2026-05-19) — "19% of reviewers expect same-day response (24h);
 *     32% expect next-day (48h)."
 *   - Harvard Business Review meta-study (cited in ReplyOnTheFly 2026) —
 *     "Responding to reviews increases star rating by an average of 0.12
 *     stars and generates 12% more reviews."
 *
 * Band semantics:
 *   - excellent (< 2h): top-performer band; automation-enabled response
 *   - good (< 24h): meets the 19%-expectation same-day threshold
 *   - fair (< 72h): meets the 32%-expectation next-day-plus-grace
 *   - poor (>= 72h): below the consumer-expected ceiling; reputational risk
 *
 * `as const` so any code attempting to mutate the export is rejected at
 * compile-time. Bar widget renderer reads these via the preset's
 * `config.thresholds` field.
 */
export const REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_THRESHOLDS_HOURS = {
  excellent: 2,
  good: 24,
  fair: 72,
} as const;

/**
 * Story 23.8 — Color palette for the Avg Response Time Bar widget's
 * response-time bands. Tailwind v4 semantic-color palette tokens:
 *   - excellent: emerald-500 (`#10B981`) — top-performer band
 *   - good: blue-500 (`#3B82F6`) — meets-expectation band
 *   - fair: amber-500 (`#F59E0B`) — at-risk band
 *   - poor: red-500 (`#EF4444`) — reputational-risk band
 *
 * The colors are intentionally NOT per-platform brand colors — the bar
 * widget uses BAND colors to communicate response-time SEVERITY (how
 * fast/slow), NOT which platform. Mixing brand colors with band colors
 * would create a 4-dimensional color encoding (platform + severity) the
 * eye can't decode at glance.
 *
 * Colorblind-safe per WebAIM Sim Daltonism check (verified 2026-05-19):
 * the 4-band emerald/blue/amber/red palette retains contrast under
 * deuteranopia + protanopia simulations.
 */
export const REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_BAND_COLORS: Record<
  "excellent" | "good" | "fair" | "poor",
  string
> = {
  excellent: "#10B981",
  good: "#3B82F6",
  fair: "#F59E0B",
  poor: "#EF4444",
};

/**
 * Story 23.8 Pass 1 Code Review M4 fix — empty-state color token.
 *
 * The Avg Response Time Bar widget MUST distinguish between:
 *   - "0 hours" (impossibly excellent — likely a fixture bug or test
 *     mode) → rendered in the `excellent` emerald band
 *   - "null" (no APPROVED replies yet; newly-listed business; all-
 *     PENDING moderation state) → rendered in this `noData` gray band
 *
 * Tailwind v4 gray-400 (`#9CA3AF`) — neutral, deliberately desaturated
 * to read as "no signal" rather than as a severity band. Renderer
 * consults this token when a per-platform `avgReplyLatencySeconds`
 * comes back as null from the production resolver.
 */
export const REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_NO_DATA_COLOR =
  "#9CA3AF" as const;
